import React, { ReactNode } from 'react'

import clsx from 'clsx'

type SocialLink = {
	link: string
	social: 'twitter' | 'facebook' | 'linkedin' | 'email' | 'reddit' | 'whatsapp'
	icon?: ReactNode
}

type Props = {
	className?: string
	heading?: ReactNode
	title: string
	url: string
	color?: string
	socialLinks?: SocialLink[]
}

export function ShareLinks({ className, color, title, url, heading }: Props) {
	const encodedTitle = encodeURIComponent(title)
	const encodedUrl = encodeURIComponent(url)
	const headingText = heading
	const socialLinks: SocialLink[] = [
		{
			link: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
			social: 'twitter',
			icon: (
				<svg className="w-8 h-8" viewBox="0 0 20 20">
					<path
						fill="currentColor"
						fillRule="evenodd"
						d="M10 20c5.523 0 10-4.477 10-10S15.523 0 10 0 0 4.477 0 10s4.477 10 10 10Zm5.957-14.217a2.42 2.42 0 0 1-1 1.35 4.31 4.31 0 0 0 1.307-.384 4.81 4.81 0 0 1-1.136 1.264c.004.104.005.21.005.316 0 3.226-2.289 6.946-6.478 6.946a6.139 6.139 0 0 1-3.49-1.097 4.378 4.378 0 0 0 3.371-1.011c-.997-.019-1.838-.726-2.127-1.695.138.029.282.043.428.043.209 0 .41-.029.6-.086-1.042-.223-1.827-1.21-1.827-2.393v-.03a2.17 2.17 0 0 0 1.032.305c-.61-.439-1.013-1.186-1.013-2.032 0-.447.112-.867.308-1.228C7.061 7.529 8.74 8.499 10.631 8.6a2.594 2.594 0 0 1-.058-.556c0-1.348 1.018-2.44 2.276-2.44.655 0 1.247.296 1.662.77a4.375 4.375 0 0 0 1.446-.592Z"
						clipRule="evenodd"
					/>
				</svg>
			)
		},
		{
			link: `http://www.linkedin.com/shareArticle?url=${encodedUrl}`,
			social: 'linkedin',
			icon: (
				<svg className="w-8 h-8" viewBox="0 0 20 20">
					<path
						fill="currentColor"
						fillRule="evenodd"
						d="M10 20c5.523 0 10-4.477 10-10S15.523 0 10 0 0 4.477 0 10s4.477 10 10 10ZM7.623 7.826H5.738v6.569h1.885V7.826ZM6.62 7.004h.014c.708 0 1.15-.504 1.15-1.14-.014-.648-.442-1.139-1.137-1.139-.696 0-1.151.493-1.151 1.141 0 .636.442 1.138 1.124 1.138Zm6.409 7.392h2.137v-3.654c0-2.034-1.053-2.979-2.392-2.979-1.08 0-1.756.643-1.882 1.094v-1.03H8.774c.027.546 0 6.569 0 6.569h2.117V10.77c0-.174-.002-.342.046-.466.146-.396.459-.805 1.021-.805.735 0 1.07.607 1.07 1.497v3.4Z"
						clipRule="evenodd"
					/>
				</svg>
			)
		},
		{
			link: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
			social: 'facebook',
			icon: (
				<svg className="w-8 h-8" viewBox="0 0 20 20">
					<path
						fill="currentColor"
						fillRule="evenodd"
						d="M10 20c5.523 0 10-4.477 10-10S15.523 0 10 0 0 4.477 0 10s4.477 10 10 10Zm-1.469-4.725h2.339v-5.33h1.56l.207-1.837H10.87l.003-.92c0-.479.048-.735.778-.735h.976V4.615h-1.561c-1.875 0-2.535.892-2.535 2.39v1.103H7.363v1.836H8.53v5.33Z"
						clipRule="evenodd"
					/>
				</svg>
			)
		},
		{
			link: `mailto:?subject=${encodedTitle}&body=${encodedUrl}`,
			social: 'email',
			icon: (
				<svg className="w-8 h-8" viewBox="0 0 20 20">
					<path
						fill="currentColor"
						fillRule="evenodd"
						d="M10 20c5.523 0 10-4.477 10-10S15.523 0 10 0 0 4.477 0 10s4.477 10 10 10ZM4.877 6.626a.798.798 0 0 1 .428-.126h9.39c.158 0 .303.048.428.126l-4.754 4.028a.55.55 0 0 1-.73 0L4.877 6.626Zm-.377.679c0-.093.017-.181.046-.264L8.038 10 4.63 13.106a.798.798 0 0 1-.13-.436V7.305Zm7.47 2.691 3.484-2.955a.803.803 0 0 1 .046.264v5.366c0 .161-.049.31-.13.436l-3.4-3.11Zm-1.257 1.069.847-.717 3.374 3.09a.806.806 0 0 1-.239.037h-9.39a.806.806 0 0 1-.24-.037l3.384-3.086.843.713a1.1 1.1 0 0 0 1.42 0Z"
						clipRule="evenodd"
					/>
				</svg>
			)
		},
		{
			link: `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`,
			social: 'reddit',
			icon: (
				<svg className="w-8 h-8" viewBox="0 0 20 20">
					<path
						fill="currentColor"
						fillRule="evenodd"
						d="M10 20c5.523 0 10-4.477 10-10S15.523 0 10 0 0 4.477 0 10s4.477 10 10 10Zm4.86-11.297c.74 0 1.34.6 1.34 1.34 0 .547-.332 1.018-.771 1.233.021.129.032.257.032.396 0 2.059-2.39 3.72-5.35 3.72-2.959 0-5.35-1.661-5.35-3.72 0-.139.01-.278.032-.407a1.34 1.34 0 0 1 .547-2.562c.354 0 .686.15.922.375.922-.675 2.198-1.094 3.624-1.136l.675-3.195a.26.26 0 0 1 .108-.15c.053-.033.117-.043.182-.033l2.22.472a.927.927 0 0 1 .846-.536c.526 0 .954.429.954.954a.955.955 0 0 1-1.908.043L10.98 5.08l-.612 2.863c1.394.053 2.66.482 3.57 1.136.236-.236.558-.375.923-.375Zm-6.872 1.34a.956.956 0 0 0-.954.954c0 .525.429.954.954.954a.956.956 0 0 0 .954-.954.956.956 0 0 0-.954-.954Zm2.112 4.17c.365 0 1.609-.042 2.263-.696a.276.276 0 0 0 .021-.354.252.252 0 0 0-.354 0c-.418.407-1.286.557-1.919.557-.632 0-1.512-.15-1.919-.557a.252.252 0 0 0-.354 0 .252.252 0 0 0 0 .354c.643.643 1.898.696 2.262.696Zm1.137-3.216c0 .525.429.954.954.954a.956.956 0 0 0 .954-.954.956.956 0 0 0-.954-.954.956.956 0 0 0-.954.954Z"
						clipRule="evenodd"
					/>
				</svg>
			)
		},
		{
			link: `whatsapp://send?text=${encodedTitle}:${encodedUrl}`,
			social: 'whatsapp',
			icon: (
				<svg className="w-8 h-8" viewBox="0 0 20 20">
					<path
						fill="currentColor"
						fillRule="evenodd"
						d="M10 20c5.523 0 10-4.477 10-10S15.523 0 10 0 0 4.477 0 10s4.477 10 10 10Zm.089-15C7.38 5 5.177 7.187 5.177 9.875c0 .87.233 1.72.673 2.465l-.843 2.488a.13.13 0 0 0 .162.166l2.595-.825c.71.38 1.513.58 2.325.58 2.708 0 4.911-2.187 4.911-4.874C15 7.187 12.797 5 10.089 5Zm0 8.734c-.764 0-1.505-.221-2.141-.639a.13.13 0 0 0-.11-.015l-1.3.413.42-1.238a.13.13 0 0 0-.02-.118 3.806 3.806 0 0 1-.74-2.262c0-2.128 1.746-3.86 3.89-3.86 2.146 0 3.89 1.732 3.89 3.86 0 2.127-1.744 3.859-3.89 3.859Zm2.31-2.988-.024-.012c-.131-.064-.737-.36-.854-.403a.504.504 0 0 0-.17-.037c-.098 0-.18.049-.245.145a6.8 6.8 0 0 1-.361.443c-.01.01-.021.022-.029.022-.006 0-.119-.046-.153-.061-.782-.34-1.376-1.157-1.457-1.294a.084.084 0 0 1-.012-.029.235.235 0 0 1 .042-.05c.04-.04.083-.092.125-.142l.059-.07c.06-.07.087-.124.118-.188l.016-.033a.34.34 0 0 0-.01-.32 68.626 68.626 0 0 1-.294-.703l-.063-.152c-.08-.188-.183-.276-.328-.276h-.007l-.049.002c-.068.003-.441.052-.606.156-.175.11-.47.462-.47 1.08 0 .556.352 1.081.504 1.28l.016.024.004.007c.58.847 1.304 1.475 2.037 1.768.707.282 1.04.315 1.23.315.08 0 .145-.007.201-.012l.036-.003c.244-.022.78-.3.902-.639.096-.267.121-.558.057-.664-.044-.072-.119-.108-.215-.154Z"
						clipRule="evenodd"
					/>
				</svg>
			)
		}
	]

	return (
		<div className={clsx(className, 'w-full self-stretch')}>
			<div className="sticky top-[150px]">
				{headingText ? (
					<span className="text-eyebrow uppercase text-bc-black font-medium">{headingText}</span>
				) : null}

				<ul className="flex flex-wrap gap-2 mt-3">
					{socialLinks?.map((socialLink, i) => {
						return (
							<li key={i} style={{ color: color }}>
								<a
									href={socialLink.link}
									target="_blank"
									rel="noopener noreferrer"
									data-testid={`share-${socialLink.social}`}
								>
									{socialLink.icon}
								</a>
							</li>
						)
					})}
				</ul>
			</div>
		</div>
	)
}
